/**
 * theme.js — Minimal vanilla JS
 * Android Auto Head Units — Premium Shopify Theme
 */
(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Utility: DOM selectors
     -------------------------------------------------------------------------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* --------------------------------------------------------------------------
     1. no-js → js
     -------------------------------------------------------------------------- */
  document.documentElement.classList.remove('no-js');
  window.addEventListener('load', function () {
    document.documentElement.classList.remove('preload');
  });

  /* --------------------------------------------------------------------------
     2. Sticky header shadow on scroll
     -------------------------------------------------------------------------- */
  function initStickyHeader() {
    const header = $('.site-header');
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --------------------------------------------------------------------------
     3. Mobile nav drawer
     -------------------------------------------------------------------------- */
  function initNavDrawer() {
    const openBtn = $('.js-nav-open');
    const closeBtn = $('.js-nav-close');
    const overlay = $('.nav-drawer__overlay');
    const drawer = $('.nav-drawer');
    if (!drawer) return;

    const open = () => {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      closeBtn && closeBtn.focus();
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      openBtn && openBtn.focus();
    };

    openBtn && openBtn.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    overlay && overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* --------------------------------------------------------------------------
     4. Accordion (FAQ, specs)
     -------------------------------------------------------------------------- */
  function initAccordions() {
    $$('.accordion-trigger').forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const content = $('#' + btn.getAttribute('aria-controls'));
        if (!content) return;

        btn.setAttribute('aria-expanded', String(!expanded));
        content.classList.toggle('is-open', !expanded);
      });
    });
  }

  /* --------------------------------------------------------------------------
     5. Product gallery — thumbnail switcher + lightbox
     -------------------------------------------------------------------------- */
  function initProductGallery() {
    const gallery = $('.product-gallery');
    if (!gallery) return;

    const mainImg = $('.product-gallery__main-img', gallery);
    const mainWrap = $('.product-gallery__main', gallery);
    const thumbs = $$('.product-gallery__thumb', gallery);

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt;
        if (!src || !mainImg) return;

        mainImg.src = src;
        const srcset = thumb.dataset.srcset;
        if (srcset) mainImg.srcset = srcset;
        if (alt) mainImg.alt = alt;

        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

  }

  /* --------------------------------------------------------------------------
     6. Variant selector
     -------------------------------------------------------------------------- */
  function initVariantSelector() {
    const form = $('.js-product-form');
    if (!form) return;

    // Variant buttons are outside the <form> element, in the parent .product-info container
    const productInfo = form.closest('.product-info') || document;
    const optionBtns = $$('.variant-option', productInfo);
    const variantInput = $('[name="id"]', form);
    const priceEl = $('.js-product-price', productInfo);
    const atcBtn = $('.js-atc-btn', form);
    const stickyAtcBtn = $('.js-sticky-atc-btn');

    const variants = window.productVariants || [];

    // Returns { positionIndex: value } only for visible option groups
    function getSelectedValues() {
      const values = {};
      optionBtns.filter((b) => b.classList.contains('is-selected')).forEach((b) => {
        // position is 1-based in Shopify; store as 0-based index
        values[parseInt(b.dataset.position, 10) - 1] = b.dataset.value;
      });
      return values;
    }

    // Match only on visible positions — hidden options (Ships From) are ignored
    function findVariant(selectedValues) {
      const entries = Object.entries(selectedValues);
      return variants.find((v) =>
        entries.every(([idx, val]) => v.options[parseInt(idx, 10)] === val)
      );
    }

    function updateUI(variant) {
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;
      if (stickyAtcBtn) stickyAtcBtn.dataset.variantId = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      // Sticky cart prices
      const stickyPriceEl = $('.js-sticky-price');
      const stickyCompareEl = $('.js-sticky-compare-price');
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);
      if (stickyCompareEl) {
        if (variant.compare_at_price > variant.price) {
          stickyCompareEl.textContent = formatMoney(variant.compare_at_price);
          stickyCompareEl.hidden = false;
        } else {
          stickyCompareEl.hidden = true;
        }
      }

      // Compare price: create element if needed, hide if not applicable
      let compareEl = productInfo ? productInfo.querySelector('.js-product-compare-price') : null;
      if (variant.compare_at_price > variant.price) {
        if (!compareEl) {
          const priceWrap = productInfo ? productInfo.querySelector('.product-info__price') : null;
          if (priceWrap) {
            compareEl = document.createElement('span');
            compareEl.className = 'product-price__compare js-product-compare-price';
            priceWrap.insertBefore(compareEl, priceWrap.firstChild);
          }
        }
        if (compareEl) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
      }

      // Switch gallery image to variant image
      const mainImg = document.getElementById('main-product-image');
      const gallery = document.querySelector('.product-gallery');
      const varThumbs = gallery ? [...gallery.querySelectorAll('.product-gallery__thumb')] : [];

      if (variant.featured_image && mainImg) {
        const matchThumb = varThumbs.find((t) => t.dataset.imageId == variant.featured_image.id);
        let newSrc;

        if (matchThumb) {
          newSrc = matchThumb.dataset.src;
          const newSrcset = matchThumb.dataset.srcset;
          if (newSrcset) mainImg.srcset = newSrcset;
          if (matchThumb.dataset.alt) mainImg.alt = matchThumb.dataset.alt;
        } else {
          const imgs = window.productVariantImages || {};
          const imgData = imgs[variant.featured_image.id] || imgs[String(variant.featured_image.id)];
          if (imgData && typeof imgData === 'object') {
            newSrc = imgData.src;
            if (imgData.srcset) mainImg.srcset = imgData.srcset;
            if (imgData.alt) mainImg.alt = imgData.alt;
          } else {
            newSrc = (imgData) || variant.featured_image.src;
            // Без цього лишався б srcset попереднього зображення, і браузер
            // показав би старий кадр: srcset перемагає src, коли дескриптор підходить.
            mainImg.srcset = '';
            if (variant.featured_image.alt) mainImg.alt = variant.featured_image.alt;
          }
        }

        if (newSrc && mainImg.src !== newSrc) {
          mainImg.src = newSrc;
        }

        varThumbs.forEach((t) => t.classList.remove('is-active'));
        if (matchThumb) matchThumb.classList.add('is-active');

        // Mobile: scroll gallery into view so user sees the updated image
        if (window.innerWidth < 768 && gallery) {
          gallery.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      const available = variant.available;
      if (atcBtn) {
        atcBtn.disabled = !available;
        atcBtn.textContent = available
          ? atcBtn.dataset.addText || 'Add to Cart'
          : atcBtn.dataset.soldText || 'Sold Out';
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.disabled = !available;
        stickyAtcBtn.dataset.variantId = variant.id;
      }

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: {
        id: variant.id,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        available: variant.available,
        imageId: variant.featured_image ? variant.featured_image.id : null
      }}));

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    function formatMoney(cents) {
      const fmt = window.__moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amountNd = String(Math.floor(cents / 100));
      const amountC = (cents / 100).toFixed(2).replace('.', ',');
      return fmt
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', amountNd)
        .replace('{{amount_with_comma_separator}}', amountC)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNd);
    }

    let _variantUpdating = false;
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (_variantUpdating) return;
        _variantUpdating = true;
        setTimeout(() => { _variantUpdating = false; }, 50);
        const pos = btn.dataset.position;
        $$(`[data-position="${pos}"]`, productInfo).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        // Update label text (e.g. "Size: 4G+32G" → "Size: 8G+128G")
        const labelStrong = btn.closest('.variant-group') && btn.closest('.variant-group').querySelector('.variant-label strong');
        if (labelStrong) labelStrong.textContent = btn.dataset.value;

        const variant = findVariant(getSelectedValues());
        if (variant) updateUI(variant);
      });
    });
  }

  /* --------------------------------------------------------------------------
     7. Quantity stepper
     -------------------------------------------------------------------------- */
  function initQtyStepper() {
    $$('.qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const field = btn.closest('.qty-input').querySelector('.qty-field');
        if (!field) return;
        let val = parseInt(field.value, 10) || 1;
        if (btn.dataset.action === 'minus') val = Math.max(1, val - 1);
        if (btn.dataset.action === 'plus') val += 1;
        field.value = val;
      });
    });
  }

  /* --------------------------------------------------------------------------
     9. Quick Add (product card hover button)
     -------------------------------------------------------------------------- */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.js-quick-add');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const data = await res.json();

        if (res.ok) {
          await updateCartCount();
          showCartNotification(data.title);
          // brief green state
          setTimeout(() => {
            btn.disabled = false;
            btn.innerHTML = originalHTML;
          }, 1500);
        } else {
          showCartNotification(data.description || 'Error adding to cart', true);
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }
      } catch {
        showCartNotification('Network error. Please try again.', true);
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });
  }

  /* --------------------------------------------------------------------------
     10. Cart count update (shared helper)
     -------------------------------------------------------------------------- */
  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      $$('.js-cart-count').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch {
      // silent
    }
  }

  /* --------------------------------------------------------------------------
     10. Cart notification toast
     -------------------------------------------------------------------------- */
  function showCartNotification(message, isError = false) {
    const el = $('#cart-notification');
    if (!el) return;

    el.textContent = message;
    el.style.backgroundColor = isError ? 'var(--color-error)' : 'var(--color-primary)';
    el.hidden = false;

    clearTimeout(el._timeout);
    el._timeout = setTimeout(() => { el.hidden = true; }, 3500);
  }

  /* --------------------------------------------------------------------------
     11. Sticky cart — show after scroll past ATC
     -------------------------------------------------------------------------- */
  function initStickyCartVisibility() {
    const stickyCart = $('.sticky-cart');
    const atcBtn = $('.js-atc-btn');
    if (!stickyCart || !atcBtn) return;

    const observer = new IntersectionObserver(([entry]) => {
      stickyCart.hidden = entry.isIntersecting;
    }, { rootMargin: '0px 0px -60px 0px' });

    observer.observe(atcBtn);
  }

  /* --------------------------------------------------------------------------
     12. Lazy image fade-in
     -------------------------------------------------------------------------- */
  function initLazyImages() {
    if (!('IntersectionObserver' in window)) {
      $$('img[loading="lazy"]').forEach((img) => img.classList.add('loaded'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const onLoad = () => img.classList.add('loaded');
          if (img.complete) onLoad();
          else img.addEventListener('load', onLoad, { once: true });
          obs.unobserve(img);
        }
      });
    });
    $$('img[loading="lazy"]').forEach((img) => obs.observe(img));
  }

  /* --------------------------------------------------------------------------
     13. Back to Top button
     -------------------------------------------------------------------------- */
  function initBackToTop() {
    var btn = document.createElement('button');
    btn.className = 'back-to-top js-back-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg>';
    document.body.appendChild(btn);
    window.addEventListener('scroll', function () {
      if (window.scrollY > 400) btn.classList.add('is-visible');
      else btn.classList.remove('is-visible');
    }, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* --------------------------------------------------------------------------
     13a. Плаваючі віджети: чат Shopify Inbox, «вгору», бейдж Google
     Позицію «вгору» відступами вгадати не можна: активатор чату міняє розмір
     (85×46 → 96×50) і на iPhone піднімається на safe-area — кнопка опинялась
     під ним. Тому ставимо її над реальним прямокутником активатора.
     Активатор лежить у вкладеному shadow DOM, звичайний querySelector його
     не бачить. Коли відкрито меню, кошик, пошук, cookie-банер чи попап —
     ховаємо всі три віджети, бо вони перекривали кнопки цих панелей.
     -------------------------------------------------------------------------- */
  function initFloatingWidgets() {
    var GAP = 12;
    var root = document.documentElement;
    var activator = null;
    var googleOrigBottom = null;

    function findActivator(node) {
      var found = node.querySelector('button.activator');
      if (found) return found;
      var all = node.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        if (all[i].shadowRoot) {
          found = findActivator(all[i].shadowRoot);
          if (found) return found;
        }
      }
      return null;
    }

    function isShown(el) {
      if (!el) return false;
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
    }

    function overlayOpen() {
      return !!document.querySelector(
        '.nav-drawer.is-open, #cart-panel.is-open, .js-search-panel:not([hidden]), .dp-overlay.is-visible'
      ) || isShown(document.getElementById('shopify-pc__banner'));
    }

    function place() {
      root.classList.toggle('floating-widgets-hidden', overlayOpen());

      var btn = document.querySelector('.js-back-to-top');
      if (!activator || !activator.isConnected) {
        var host = document.querySelector('shopify-chat');
        activator = host && host.shadowRoot ? findActivator(host.shadowRoot) : null;
      }
      if (btn) {
        if (activator && isShown(activator)) {
          var a = activator.getBoundingClientRect();
          btn.style.bottom = Math.round(window.innerHeight - a.top + GAP) + 'px';
          btn.style.right = Math.round(window.innerWidth - a.right + (a.width - btn.offsetWidth) / 2) + 'px';
        } else {
          btn.style.bottom = '';
          btn.style.right = '';
        }
      }

      /* Бейдж Google задає bottom інлайном з !important — переписати можна
         лише так само інлайном. Піднімаємо його над панеллю ADD TO CART. */
      var g = document.getElementById('google-merchantwidget-iframe-wrapper');
      if (g) {
        if (googleOrigBottom === null) googleOrigBottom = g.style.getPropertyValue('bottom');
        var bar = document.querySelector('.sticky-atc.is-visible');
        var want = bar ? Math.round(bar.getBoundingClientRect().height + GAP) + 'px' : googleOrigBottom;
        if (want && g.style.getPropertyValue('bottom') !== want) g.style.setProperty('bottom', want, 'important');
      }
    }

    var ticking = false;
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; place(); });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('click', function () { setTimeout(schedule, 350); });
    /* Чат, бейдж і cookie-банер вантажаться із запізненням і змінюють стан
       без подій, на які можна підписатись. */
    setInterval(function () { if (!document.hidden) place(); }, 700);
    place();
  }

  /* --------------------------------------------------------------------------
     14. Init all on DOM ready
     -------------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     Add to cart — loading state
     Основна форма товару сабмітиться нативно, тож між кліком і переходом
     кнопка виглядала «мертвою». Стан тримається до навігації; на випадок
     скасованого переходу (bfcache, помилка валідації) є таймаут-відновлення.
     -------------------------------------------------------------------------- */
  function initProductFormLoading() {
    const form = $('.js-product-form');
    if (!form) return;
    const btn = $('.js-atc-btn', form);
    if (!btn) return;

    form.addEventListener('submit', () => {
      if (btn.dataset.loading === '1') return;
      btn.dataset.loading = '1';
      btn.dataset.originalHtml = btn.innerHTML;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;

      setTimeout(() => restore(btn), 8000);
    });

    // Повернення на сторінку з кешу браузера не має лишати кнопку заблокованою
    window.addEventListener('pageshow', () => restore(btn));

    function restore(b) {
      if (b.dataset.loading !== '1') return;
      delete b.dataset.loading;
      b.classList.remove('is-loading');
      b.removeAttribute('aria-busy');
      b.disabled = false;
      if (b.dataset.originalHtml) b.innerHTML = b.dataset.originalHtml;
    }
  }

  function init() {
    initStickyHeader();
    initNavDrawer();
    initAccordions();
    initProductGallery();
    initVariantSelector();
    initQtyStepper();
    initQuickAdd();
    initProductFormLoading();
    initStickyCartVisibility();
    initLazyImages();
    updateCartCount();
    initBackToTop();
    initFloatingWidgets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
